import React, { useEffect } from "react";
import { t } from "i18next";
import { Text } from "../../Atomics/Text";
import { Col } from "antd";
import useFetch from "../../../../server/useFetch";
import Theme from "../../../../styles/Theme/Theme";
import { isEmpty } from "../../../../utils/hooks/isEmpty";
import { NewWidget } from "../../Atomics/NewWidget";
import { containerMiddlewareAdEPortoEnergy } from "../../../../server/constants.api";
import { unitConverterArrays } from "../../../../utils/hooks/unitConverter";

export const CumulativeAndComparativeAnalysis = ({ filtersSelected }) => {
  const { data: analyticsComparative, isFetching: analyticsComparativeIsFetching, refetch: refetchAnalyticsComparative } = useFetch(
    "analytics/comparative",
    "analyticsComparative",
    {
      metric: filtersSelected?.metric,
      periodicity: filtersSelected?.periodicity,
      startDate: filtersSelected?.startDate,
      endDate: filtersSelected?.endDate
    },
    true,
    'POST',
    containerMiddlewareAdEPortoEnergy,
    filtersSelected?.ids
  );

  useEffect(() => {
    if (filtersSelected) {
      refetchAnalyticsComparative();
    }
  }, [filtersSelected]);

  return (
    <>
      <Col span={24}>
        <div className="title">
          <Text
            $fontColor="coolgray-12"
            $fontSize="text_xl"
            $weight="regular"
            $marginRight="s03"
            $marginBottom="s08"
          >
            {t("Cumulative Analysis")}
          </Text>
        </div>
        <NewWidget
          data={{
            id: 13,
            width: "auto",
            background: "white",
            icon: null,
            charts: [
              {
                height: "447px",
                chartType: "mixedLineColumnChartApex",
                isFetching: analyticsComparativeIsFetching,
                data: analyticsComparative?.data?.total && !isEmpty(analyticsComparative?.data?.total) ?
                  serializeAnalyticCumulativeChartData(analyticsComparative?.data, filtersSelected?.periodicity) : null
              },
            ]
          }}
        />
      </Col>
      <Col span="24">
        <div className="title">
          <Text
            $fontColor="coolgray-12"
            $fontSize="text_xl"
            $weight="regular"
            $marginRight="s03"
            $marginBottom="s08"
          >
            {t("Comparative analysis")}
          </Text>
        </div>
        <NewWidget
          data={{
            id: 13,
            width: "auto",
            background: "white",
            icon: null,
            charts: [
              filtersSelected?.ids?.length < 6 ?
                {
                  height: "447px",
                  chartType: "mixedLineColumnChartApex",
                  isFetching: analyticsComparativeIsFetching,
                  data: analyticsComparative?.data?.total && !isEmpty(analyticsComparative?.data?.total) ?
                    serializeAnalyticComparativeChartData(analyticsComparative?.data, filtersSelected?.periodicity) : null
                } : {
                  chartType: 'bigNumberChart',
                  chartId: 'bigNumberChartOne',
                  data: {
                    number: '',
                    label: t('Limit of 5 items'),
                    icon: 'Alert'
                  }
                }
            ]
          }}
        />
      </Col>
    </>
  );
};

const serializeAnalyticCumulativeChartData = (data, periodicity) => {
  const seriesData = [];
  const labels = [];

  const sumPerDate = regroupByDate(data.total, periodicity);

  //Create series
  Object.entries(sumPerDate).map(([key, value]) => {
    seriesData.push(value);
    labels.push(key);
  });

  //Convert unit
  const { convertedResult, newUnit } = unitConverterArrays([seriesData], data.unit);

  return {
    series: [
      {
        name: t("Total Accumulated"),
        type: "line",//scatter or line or column
        data: convertedResult[0],
      }
    ],
    categories: null,
    // xAxisType: "datetime",
    labelFormat: "dd/MM",
    tooltip: "dd/MM/yyyy",
    markerSize: 3,
    unit: newUnit,
    "yAxisLabel": newUnit,
    yAxisLabelRotate: -90,
    columnWidth: 20,
    colors: [Theme.color["blue-4"]],
    labels: labels,
    markersSize: [6, 6],
    hideToolbar: false,
    tickAmount: periodicity === 'HOURLY' ? 20 : null
  }
};

function regroupByDate(dados, periodicity) {
  const result = {};

  for (const [timestampStr, value] of Object.entries(dados)) {
    // Converter o timestamp para milissegundos (se o timestamp for em segundos)
    const timestamp = Number(timestampStr) * 1000;

    // Criar um objeto Date
    const data = new Date(timestamp);

    // Extrair dia, mês e ano
    const dia = data.getUTCDate();
    const mes = data.getUTCMonth() + 1; // Mês começa em 0
    const ano = data.getUTCFullYear();

    // Criar uma chave única por data
    var dateKey;

    if (periodicity === 'MONTHLY') {
      dateKey = `${ano}-${mes.toString().padStart(2, '0')}`
    } else if (periodicity === 'HOURLY') {
      dateKey = `${dia.toString().padStart(2, '0')}-${(mes).toString().padStart(2, '0')} ${data.getHours().toString().padStart(2, '0')}:${data.getMinutes().toString().padStart(2, '0')}`;
    } else {
      dateKey = data.toISOString()?.split('T')[0];
    }

    // Somar os valores que têm a mesma data
    if (!result[dateKey]) {
      result[dateKey] = 0;
    }
    result[dateKey] += value;
  }

  return result;
};

const serializeAnalyticComparativeChartData = (data, periodicity) => {
  const series = [];
  const labels = [];
  Object.keys(data?.total).map((key) => {
    const convertedTimestamp = new Date(Number(key) * 1000);

    var dateResult;
    if (periodicity === 'MONTHLY') {
      dateResult = `${convertedTimestamp.getDate().toString().padStart(2, '0')}-${(convertedTimestamp.getMonth() + 1).toString().padStart(2, '0')}-${convertedTimestamp.getFullYear()}`;
    } else if (periodicity === 'HOURLY') {
      dateResult = `${convertedTimestamp.getDate().toString().padStart(2, '0')}-${(convertedTimestamp.getMonth() + 1).toString().padStart(2, '0')} ${convertedTimestamp.getHours().toString().padStart(2, '0')}:${convertedTimestamp.getMinutes().toString().padStart(2, '0')}`;
    } else {
      dateResult = convertedTimestamp.toISOString()?.split('T')[0];
    }

    labels.push(dateResult);
  });

  //Get array of values to convert unit
  const arrayOfValues = Object.entries(data?.data).map(([key, value]) => {
    return Object.values(value)
  });
  const { convertedResult, newUnit } = unitConverterArrays(arrayOfValues, data.unit);

  //Create series
  Object.entries(data?.data).map(([key, value], index) => {
    !isEmpty(value) && series.push({
      name: key,
      type: "line",
      data: convertedResult[index]
    })
  });

  return {
    series: series,
    categories: null,
    // xAxisType: "datetime",
    labelFormat: "dd/MM",
    tooltip: "dd/MM/yyyy",
    markerSize: 3,
    unit: newUnit,
    "yAxisLabel": newUnit,
    yAxisLabelRotate: -90,
    columnWidth: 20,
    colors: series?.map((x, index) => { return Theme.color[`chart-base-${index + 1}`] }),
    labels: labels,
    markersSize: [6, 6],
    hideToolbar: false,
    tickAmount: periodicity === 'HOURLY' ? 20 : null
  }
};